import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { ErrorState, Loading, PageHeader, Panel } from '../components/ui';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import { formatDateTime, formatRelative } from '../lib/format';

type SystemHealth = {
  infrastructure: { name: string; healthy: boolean; latencyMs: number | null; detail: string | null }[];
  services: { service: string; healthy: boolean; detail: string | null; observedAt: string; meta: unknown }[];
  cameras: Record<string, number>;
};

type AuditRow = {
  id: string;
  actorEmail: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  ipAddress: string | null;
  createdAt: string;
};

export function SystemPage() {
  const { can } = useAuth();

  const healthQuery = useQuery({
    queryKey: ['system', 'health'],
    queryFn: () => api.get<SystemHealth>('/system/health'),
    refetchInterval: 30_000,
  });

  const auditQuery = useQuery({
    queryKey: ['system', 'audit'],
    queryFn: () => api.get<{ items: AuditRow[] }>('/system/audit?limit=50'),
    enabled: can('audit:read'),
  });

  if (healthQuery.isLoading) return <Loading label="Checking system health" />;
  if (healthQuery.isError) {
    return <ErrorState message={(healthQuery.error as Error).message} onRetry={() => void healthQuery.refetch()} />;
  }

  const health = healthQuery.data!;

  return (
    <div className="space-y-6">
      <PageHeader
        title="System Health"
        subtitle="A service that stops reporting is treated as unhealthy — a crashed process never gets to say it failed."
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Infrastructure">
          <ul className="divide-y divide-line/50">
            {health.infrastructure.map((item) => (
              <li key={item.name} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="flex items-center gap-3">
                  <span
                    className={clsx('h-2.5 w-2.5 rounded-full', item.healthy ? 'bg-online' : 'bg-critical')}
                    aria-hidden
                  />
                  <div>
                    <p className="text-sm font-semibold">{item.name}</p>
                    {item.detail ? <p className="text-xs text-critical">{item.detail}</p> : null}
                  </div>
                </div>
                <span className="font-mono text-xs text-muted">
                  {item.latencyMs === null ? '' : `${item.latencyMs} ms`}
                </span>
              </li>
            ))}
          </ul>
          <p className="border-t border-line px-4 py-2.5 text-xs text-muted">
            go2rtc powers live view only. If it is down, detection and alerting continue unaffected.
          </p>
        </Panel>

        <Panel title="Services">
          {health.services.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted">
              No service has reported yet. Start the event processor, notifier and vision service.
            </p>
          ) : (
            <ul className="divide-y divide-line/50">
              {health.services.map((service) => (
                <li key={service.service} className="px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span
                        className={clsx(
                          'h-2.5 w-2.5 rounded-full',
                          service.healthy ? 'bg-online' : 'bg-critical',
                        )}
                        aria-hidden
                      />
                      <p className="text-sm font-semibold">{service.service}</p>
                    </div>
                    <span className="font-mono text-xs text-muted">
                      {formatRelative(service.observedAt)}
                    </span>
                  </div>
                  {service.detail ? <p className="mt-1 text-xs text-critical">{service.detail}</p> : null}
                  {service.meta && Object.keys(service.meta as object).length > 0 ? (
                    <pre className="mt-2 overflow-x-auto rounded bg-ink/60 p-2 font-mono text-[11px] text-muted">
                      {JSON.stringify(service.meta, null, 2)}
                    </pre>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <Panel title="Cameras by status">
        <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 lg:grid-cols-6">
          {['ONLINE', 'DEGRADED', 'CONNECTING', 'OFFLINE', 'ERROR', 'DISABLED'].map((status) => (
            <div key={status} className="rounded-lg border border-line bg-ink/40 px-3 py-3">
              <p className="label">{status}</p>
              <p className="mt-1 font-display text-2xl">{health.cameras[status] ?? 0}</p>
            </div>
          ))}
        </div>
      </Panel>

      {can('audit:read') ? (
        <Panel title="Audit log">
          {auditQuery.isLoading ? (
            <Loading />
          ) : (auditQuery.data?.items.length ?? 0) === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted">Nothing recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px]">
                <thead>
                  <tr>
                    <th className="table-header">When</th>
                    <th className="table-header">Who</th>
                    <th className="table-header">Action</th>
                    <th className="table-header">Entity</th>
                    <th className="table-header">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {auditQuery.data!.items.map((row) => (
                    <tr key={row.id}>
                      <td className="table-cell whitespace-nowrap font-mono text-xs text-muted">
                        {formatDateTime(row.createdAt)}
                      </td>
                      <td className="table-cell text-sm">{row.actorEmail ?? 'system'}</td>
                      <td className="table-cell font-mono text-xs">{row.action}</td>
                      <td className="table-cell text-xs text-muted">
                        {row.entityType}
                        {row.entityId ? <span className="block">{row.entityId.slice(0, 8)}…</span> : null}
                      </td>
                      <td className="table-cell font-mono text-xs text-muted">{row.ipAddress ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="border-t border-line px-4 py-2.5 text-xs text-muted">
            Append-only. Camera viewing, alert acknowledgement, configuration changes and sign-ins are
            all recorded.
          </p>
        </Panel>
      ) : null}
    </div>
  );
}
